/**
 * SO-04 / Flow E share. Web builds: the device's own share sheet (Web Share, with the
 * card file when it can be shared), then the clipboard. Native builds: the card is
 * written to the cache directory (@capacitor/filesystem) and shared as a file URI with
 * the text and link through @capacitor/share, falling back to text + link only, then
 * the clipboard. Card rendering lives in the game; this only delivers what it is given.
 */
import type { Loader } from '../lazy.js';

export interface ShareData {
  title: string;
  text: string;
  url: string;
  /** e.g. the 1080×1920 card as image/png (Web Share Level 2) */
  files?: File[];
}

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed';

export interface ShareProvider {
  share(d: ShareData): Promise<ShareOutcome>;
}

/** The part of @capacitor/share used here, as in its definitions.d.ts. */
export interface CapacitorShareModule {
  Share: { share(o: { title?: string; text?: string; url?: string; files?: string[]; dialogTitle?: string }): Promise<unknown> };
}
/** The part of @capacitor/filesystem used here; `directory` is a Directory enum value. */
export interface CapacitorFilesystemModule {
  Filesystem: { writeFile(o: { path: string; data: string | Blob; directory?: string; recursive?: boolean }): Promise<{ uri: string }> };
}
/** Directory.Cache: the OS may purge it; Android's FileProvider (`<cache-path>`) serves it to the target app. */
export const SHARE_CACHE_DIRECTORY = 'CACHE';
/** One fixed folder, files overwritten per name: the target app may read a file after the sheet closes. */
export const SHARE_CACHE_FOLDER = 'share';

type WebShareData = { title?: string; text?: string; url?: string; files?: File[] };

/** The Web Share + async Clipboard surface of `navigator` (which satisfies it). */
export interface ShareNavigator {
  share?(data: WebShareData): Promise<void>;
  canShare?(data: WebShareData): boolean;
  clipboard?: { writeText(text: string): Promise<void> };
}

export interface ShareEnv {
  navigator?: ShareNavigator;
  /** set on native builds: loads @capacitor/share (and the native path is tried first) */
  capacitorShare?: Loader<CapacitorShareModule> | null;
  /** native builds: loads @capacitor/filesystem to hand the card over as a file */
  filesystem?: Loader<CapacitorFilesystemModule> | null;
  document?: Document;
}

const isAbort = (e: unknown) => (e as { name?: string }).name === 'AbortError';
const isNativeCancel = (e: unknown) => /cancel/i.test(String((e as { message?: string }).message ?? e));

/** Text for the clipboard fallback: the message followed by the link. */
export function clipboardText(d: Pick<ShareData, 'text' | 'url'>): string {
  return d.text.includes(d.url) ? d.text : `${d.text} ${d.url}`.trim();
}

export class DeviceShareProvider implements ShareProvider {
  constructor(private readonly env: ShareEnv = {}) {}

  private get nav(): ShareNavigator {
    return this.env.navigator ?? (typeof navigator === 'undefined' ? {} : navigator);
  }

  async share(d: ShareData): Promise<ShareOutcome> {
    const sheet = this.env.capacitorShare ? await this.nativeShare(this.env.capacitorShare, d) : await this.webShare(d);
    return sheet ?? this.copy(d);
  }

  /** Web Share (Level 2 when files can be shared). null = not available here, try the next channel. */
  private async webShare(d: ShareData): Promise<ShareOutcome | null> {
    const nav = this.nav;
    if (typeof nav.share !== 'function') return null;
    const base = { title: d.title, text: d.text, url: d.url };
    const withFiles = d.files?.length && nav.canShare?.({ ...base, files: d.files }) ? { ...base, files: d.files } : null;
    const data = withFiles ?? base;
    if (nav.canShare && !nav.canShare(data)) return null;
    return nav.share(data).then(
      () => 'shared' as const,
      (e: unknown) => (isAbort(e) ? ('cancelled' as const) : null), // NotAllowedError etc.: fall through
    );
  }

  /** Capacitor Share with the card as a cached file, then text + link alone. null = neither worked, try the clipboard. */
  private async nativeShare(load: Loader<CapacitorShareModule>, d: ShareData): Promise<ShareOutcome | null> {
    const { Share } = await load();
    const base = { title: d.title, text: d.text, url: d.url, dialogTitle: d.title };
    const send = (o: typeof base & { files?: string[] }) =>
      Share.share(o).then(
        () => 'shared' as const,
        (e: unknown) => (isNativeCancel(e) ? ('cancelled' as const) : null),
      );
    const files = await this.cacheFiles(d.files);
    return (files.length ? await send({ ...base, files }) : null) ?? send(base);
  }

  /** file:// URIs of the cards in the cache directory; none when there is nothing to write or the write fails (text-only share). */
  private async cacheFiles(files: File[] | undefined): Promise<string[]> {
    if (!files?.length || !this.env.filesystem) return [];
    const { Filesystem } = await this.env.filesystem();
    const write = async (f: File) =>
      (await Filesystem.writeFile({ path: `${SHARE_CACHE_FOLDER}/${safeFileName(f.name)}`, data: await base64(f), directory: SHARE_CACHE_DIRECTORY, recursive: true })).uri;
    return Promise.all(files.map(write)).catch(() => []);
  }

  private async copy(d: ShareData): Promise<ShareOutcome> {
    const text = clipboardText(d);
    const clip = this.nav.clipboard;
    if (clip && (await clip.writeText(text).then(() => true, () => false))) return 'copied';
    return legacyCopy(text, this.env.document ?? (typeof document === 'undefined' ? null : document)) ? 'copied' : 'failed';
  }
}

/** execCommand('copy') for old WebViews without the async Clipboard API. */
function legacyCopy(text: string, doc: Document | null): boolean {
  if (!doc?.body) return false;
  const ta = doc.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
  doc.body.appendChild(ta);
  ta.select();
  const ok = typeof doc.execCommand === 'function' && doc.execCommand('copy');
  ta.remove();
  return ok;
}

/** Filesystem.writeFile takes binary data as base64 (no `encoding`). */
async function base64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

const safeFileName = (name: string): string => name.replace(/[^\w.-]+/g, '_').replace(/^\.+/, '') || 'market-pop.png';
